import { Replayer } from '@rrweb/replay'
import { SupportError } from './types.js'
import type { SupportPlayback, SupportReport } from './types.js'
import { parseReport, serializeReport } from './report.js'
const redaction = `.ae-support-redacted { background: repeating-conic-gradient(#a0a7af 0% 25%,#c7ccd2 0% 50%) 0 0 / 12px 12px !important; overflow:hidden !important; }`

/** No toolbar, dialogs, navigation, or window management. The caller owns target. */
export async function createPlayback({ report: input, target }: { report: SupportReport; target: HTMLElement }): Promise<SupportPlayback> {
  const report = parseReport(serializeReport(input))
  if (!target?.ownerDocument || !target.isConnected) throw new SupportError('InvalidOptions', 'Playback requires a connected target element')
  const frame = target.ownerDocument.createElement('iframe')
  frame.className = 'ae-support-playback rr-block'
  frame.setAttribute('sandbox', 'allow-same-origin')
  frame.setAttribute('title', 'Support recording')
  frame.style.cssText = 'width:100%;height:100%;border:0;display:block'
  target.appendChild(frame)
  let player: Replayer
  try {
    const doc = frame.contentDocument
    if (!doc) throw new Error('Playback document unavailable')
    doc.open()
    doc.write('<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="default-src &#39;none&#39;; style-src &#39;unsafe-inline&#39;; img-src data:; font-src data:; frame-src &#39;self&#39; about:; form-action &#39;none&#39;; base-uri &#39;none&#39;"><style>html,body{margin:0;width:100%;height:100%;overflow:hidden}.replayer-wrapper{position:relative;transform-origin:top left}.replayer-wrapper>iframe{border:0;pointer-events:none}.replayer-mouse{position:absolute;width:12px;height:12px;background:#333;border:2px solid white;border-radius:50%;z-index:10;pointer-events:none}</style></head><body></body></html>')
    doc.close()
    player = new Replayer(report.events as any, {
      root: doc.body, UNSAFE_replayCanvas: false, insertStyleRules: [redaction],
      mouseTail: false, triggerFocus: false, showWarning: false,
    })
    const viewport = { ...report.events[0].data }
    let view: { zoom: number; centerX: number; centerY: number } | null = null
    const fit = () => {
      const width = frame.clientWidth
      const height = frame.clientHeight
      const base = Math.min(width / viewport.width, height / viewport.height)
      const scale = (Number.isFinite(base) && base > 0 ? base : 1) * (view?.zoom || 1)
      const x = view ? width / 2 - view.centerX * viewport.width * scale : 0
      const y = view ? height / 2 - view.centerY * viewport.height * scale : 0
      player.wrapper.style.transformOrigin = '0 0'
      player.wrapper.style.transform = `translate(${x}px, ${y}px) scale(${scale})`
    }
    player.on('resize', ({ width, height }) => { viewport.width = width; viewport.height = height; fit() })
    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(fit); observer.observe(frame) }
    fit()
    let disposed = false
    let running = false
    let position = 0
    let timer: ReturnType<typeof setInterval> | null = null
    const listeners = new Set<(timeMs: number) => void>()
    const check = () => { if (disposed) throw new SupportError('Destroyed', 'Playback has been destroyed') }
    const notify = () => { for (const listener of listeners) try { listener(position) } catch {} }
    const halt = () => { if (timer) clearInterval(timer); timer = null; running = false }
    const api: SupportPlayback = {
      setView(options) {
        check()
        if (options && (!Number.isFinite(options.zoom) || options.zoom < 1 || options.zoom > 100 || !Number.isFinite(options.centerX) || options.centerX < 0 || options.centerX > 1 || !Number.isFinite(options.centerY) || options.centerY < 0 || options.centerY > 1)) throw new SupportError('InvalidOptions', 'Invalid playback view')
        view = options ? { ...options } : null
        fit()
      },
      play() {
        check()
        if (running) return
        if (position >= report.durationMs) position = 0
        player.play(position)
        running = true
        timer = setInterval(() => {
          position = Math.min(report.durationMs, Math.max(0, player.getCurrentTime()))
          if (position >= report.durationMs) { player.pause(position); halt() }
          notify()
        }, 50)
      },
      pause() { check(); if (running) position = Math.min(report.durationMs, Math.max(0, player.getCurrentTime())); player.pause(position); halt(); notify() },
      seek(timeMs) {
        check()
        if (!Number.isFinite(timeMs) || timeMs < 0 || timeMs > report.durationMs) throw new SupportError('InvalidOptions', 'Playback time is outside the report')
        const resume = running
        halt(); position = timeMs; player.pause(position); notify()
        if (resume && position < report.durationMs) api.play()
      },
      get timeMs() { return position },
      get durationMs() { return report.durationMs },
      get annotations() { return report.annotations.filter(a => a.startMs <= position && a.endMs >= position).map(a => ({ ...a })) },
      subscribe(listener) { check(); listeners.add(listener); try { listener(position) } catch {}; return () => { listeners.delete(listener) } },
      destroy() { if (disposed) return; disposed = true; halt(); observer?.disconnect(); listeners.clear(); player.destroy(); frame.remove() },
    }
    player.pause(0)
    return api
  } catch {
    player?.destroy()
    frame.remove()
    throw new SupportError('InvalidReport', 'Report could not be rendered')
  }
}
